import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ClipboardList,
  Database,
  FileBarChart2,
  LogOut,
  Plane,
  Receipt,
  Settings,
  Users,
  UserCog,
  Menu,
  X,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Ordens de Serviço", icon: ClipboardList, roles: ["admin", "operator", "office"], testid: "nav-work-orders" },
  { to: "/faturamento", label: "Faturamento", icon: Receipt, roles: ["admin", "office"], testid: "nav-billing" },
  { to: "/relatorios", label: "Relatórios & ANAC", icon: FileBarChart2, roles: ["admin", "office"], testid: "nav-reports" },
  { to: "/cadastros", label: "Cadastros", icon: Database, roles: ["admin", "office"], testid: "nav-master-data" },
  { to: "/usuarios", label: "Usuários", icon: Users, roles: ["admin"], testid: "nav-users" },
  { to: "/configuracoes", label: "Configurações", icon: Settings, roles: ["admin"], testid: "nav-settings" },
  { to: "/conta", label: "Minha Conta", icon: UserCog, roles: ["admin", "operator", "office"], testid: "nav-account" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-slate-50 ag-grain">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex flex-col w-64 ag-metal-bg border-r ag-metal-divider">
        <div className="px-6 py-6 border-b ag-metal-divider flex items-center gap-3">
          <img
            src="/aerogestor-symbol.jpg"
            alt="AeroGestor"
            className="w-11 h-11 rounded-sm object-cover ring-1 ring-white/15"
            data-testid="sidebar-logo"
          />
          <div>
            <div className="font-display font-black text-xl leading-none tracking-tight ag-brand-metallic">AeroGestor</div>
            <div className="text-[10px] tracking-[0.22em] uppercase ag-metal-overline mt-1">Hangar OS</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors border-l-2",
                  isActive
                    ? "bg-white/8 text-white font-medium border-sky-400"
                    : "text-slate-300 hover:bg-white/5 hover:text-white border-transparent"
                )
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t ag-metal-divider px-4 py-3">
          <div className="text-xs font-semibold text-slate-100 truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] ag-metal-overline mb-2" data-testid="sidebar-user-role">{user?.role}</div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-sm justify-start bg-transparent border-white/15 text-slate-100 hover:bg-white/10 hover:text-white hover:border-white/25"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 ag-metal-bg border-b ag-metal-divider px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/aerogestor-symbol.jpg" alt="AeroGestor" className="w-9 h-9 rounded-sm object-cover ring-1 ring-white/15" />
          <span className="font-display font-black text-lg tracking-tight ag-brand-metallic">AeroGestor</span>
        </div>
        <button
          className="w-10 h-10 flex items-center justify-center rounded-sm border border-white/20 text-slate-100 hover:bg-white/10"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-20 pt-14 bg-black/60" onClick={() => setOpen(false)}>
          <nav className="ag-metal-bg border-b ag-metal-divider px-3 py-3 space-y-1" onClick={(e) => e.stopPropagation()}>
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-${n.testid}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-3 text-base rounded-sm border-l-2",
                    isActive
                      ? "bg-white/10 text-white border-sky-400"
                      : "text-slate-300 hover:bg-white/5 hover:text-white border-transparent"
                  )
                }
              >
                <n.icon className="w-5 h-5" />
                {n.label}
              </NavLink>
            ))}
            <div className="border-t ag-metal-divider pt-3 mt-2">
              <div className="px-3 text-xs text-slate-300">{user?.name} ({user?.role})</div>
              <Button
                variant="outline"
                className="w-full mt-2 h-12 bg-transparent border-white/15 text-slate-100 hover:bg-white/10 hover:text-white"
                onClick={handleLogout}
                data-testid="mobile-logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </Button>
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 min-w-0">{children}</main>
    </div>
  );
}
